import "./data-entry.scss";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  atomDossier,
  atomEvaluatePremium,
  defaultValueInsured,
} from "src/storage";
import { Formik, FormikInsurance, PeriodType } from "src/types";
import { useEffect, useState } from "react";
import { useBoolean } from "src/utils";
import {
  toEvaluatePremiumRequest,
  usePostEvaluatePremium,
} from "src/networking/terminals";
import { useScrollToError } from "../handlers/scroll-to-error.hook";
import { usePeriodOptions } from "../handlers/form-label-mapping.hook";
import { GIVE_BASE_CODE, GIVE_PLUS_CODE } from "src/config";

export const useDataEntry = ({
  currentStep,
  insuranceFormik,
}: {
  currentStep: number;
  insuranceFormik: Formik<FormikInsurance>;
}) => {
  const { t } = useTranslation();

  // Starting values ================================================
  const urlSearchParams = new URLSearchParams(useLocation().search);
  const fromLanding = urlSearchParams.get("landing") === "true";
  const base = urlSearchParams.get("base") === "true";

  useEffect(() => {
    if (fromLanding) {
      insuranceFormik?.setFieldValue(
        "coverageTypeRadios",
        base ? GIVE_BASE_CODE : GIVE_PLUS_CODE
      );
      insuranceFormik?.setFieldValue("insuredPeriodType", PeriodType.DAILY);
    }
  }, [fromLanding]);

  const { values, setFieldValue } = insuranceFormik;
  const { triggerFormikCheck } = useScrollToError(insuranceFormik);

  // Evaluate Premium ===============================================
  const dossier = useRecoilValue(atomDossier);
  const [evaluating, turnOnEvaluating, turnOffEvaluating] = useBoolean();
  const [updatePremium, setUpdatePremium] = useState(false);
  const { crossGate: postEvaluatePremium } = usePostEvaluatePremium();
  const [evaluatedPremium, saveEvaluatePremium] =
    useRecoilState(atomEvaluatePremium);
  const evaluatePremium = () => {
    setUpdatePremium(true);
  };
  const doPostEvaluatePremium = () => {
    const body_ = toEvaluatePremiumRequest(values);
    if (body_) {
      postEvaluatePremium(
        {
          headers: {
            xDossierId: dossier?.dossierId || "",
          },
          body: body_,
        },
        {
          onSuccess: saveEvaluatePremium,
          onPending: turnOnEvaluating,
          onIdle: turnOffEvaluating,
        }
      );
    }
  };
  useEffect(() => {
    if (updatePremium) {
      doPostEvaluatePremium();
      setUpdatePremium(false);
    }
  }, [
    updatePremium,
    values?.coverageTypeRadios,
    values?.insuredNumber,
    values?.insuredPeriodType,
    values?.insuredDaysNumber,
  ]);

  // Date ===========================================================
  const { periodOptions } = usePeriodOptions();

  // Insureds ===================================================
  // Set insured list default values on insuredNumber changes
  const continueDisabled = currentStep === 1 && values.insuredList?.length < 1;
  useEffect(() => {
    const tot = Math.max(Number(values.insuredNumber), 0);
    const currentList = values.insuredList;

    if (currentList.length < tot) {
      const newItems = Array.from(
        { length: tot - currentList.length },
        (_) => defaultValueInsured
      );

      setFieldValue("insuredList", [...currentList, ...newItems]);
    } else if (currentList.length > tot) {
      setFieldValue("insuredList", currentList.slice(0, tot));
    }
  }, [values.insuredNumber]);

  return {
    t,
    evaluating,
    evaluatedPremium,
    periodOptions,
    continueDisabled,
    doPostEvaluatePremium,
    evaluatePremium,
    triggerFormikCheck,
  };
};
